/**
 * The running order of the show and how the 120 minutes are divided between the
 * thirteen segments.
 *
 * Weights are relative, not absolute: `buildSchedule` normalises them so the
 * season always lands exactly on the configured duration. That means an operator
 * can run a 45 minute demo season or a 3 hour special without re-tuning any
 * individual phase.
 */
import type { PhaseDefinition, PhaseId } from './types.js';
import { ROYALE_PHASES } from './royale.js';

const ROYALE_BY_ID = new Map(ROYALE_PHASES.map((p) => [p.id, p]));

export const PHASES: readonly PhaseDefinition[] = [
  {
    id: 'opening',
    order: 1,
    kind: 'ceremony',
    label: 'Opening Ceremony',
    shortLabel: 'OPENING',
    weight: 3,
    entrants: 0,
    survivors: 194,
  },
  {
    id: 'continents',
    order: 2,
    kind: 'ceremony',
    label: 'Continent Introduction',
    shortLabel: 'CONTINENTS',
    weight: 6,
    entrants: 194,
    survivors: 194,
  },
  {
    id: 'qualification_1',
    order: 3,
    kind: 'survival',
    label: 'Global Qualification',
    shortLabel: 'QUALIFY 194>70',
    weight: 29,
    entrants: 194,
    survivors: 70,
  },
  {
    id: 'qualification_2',
    order: 4,
    kind: 'survival',
    label: 'Final Qualification',
    shortLabel: 'QUALIFY 70>32',
    weight: 18,
    entrants: 70,
    survivors: 32,
  },
  {
    id: 'draw',
    order: 5,
    kind: 'ceremony',
    label: 'Draw Ceremony',
    shortLabel: 'DRAW',
    weight: 4,
    entrants: 32,
    survivors: 32,
  },
  {
    id: 'round_of_32',
    order: 6,
    kind: 'bracket',
    label: 'Round of 32',
    shortLabel: 'R32',
    weight: 16,
    entrants: 32,
    survivors: 16,
  },
  {
    id: 'round_of_16',
    order: 7,
    kind: 'bracket',
    label: 'Round of 16',
    shortLabel: 'R16',
    weight: 12,
    entrants: 16,
    survivors: 8,
  },
  {
    id: 'quarter_finals',
    order: 8,
    kind: 'bracket',
    label: 'Quarter Finals',
    shortLabel: 'QF',
    weight: 9,
    entrants: 8,
    survivors: 4,
  },
  {
    id: 'semi_finals',
    order: 9,
    kind: 'bracket',
    label: 'Semi Finals',
    shortLabel: 'SF',
    weight: 6.5,
    entrants: 4,
    survivors: 2,
  },
  {
    id: 'grand_final',
    order: 10,
    kind: 'bracket',
    label: 'Grand Final',
    shortLabel: 'FINAL',
    weight: 6.5,
    entrants: 2,
    survivors: 1,
  },
  {
    id: 'champion',
    order: 11,
    kind: 'ceremony',
    label: 'Champion Ceremony',
    shortLabel: 'CHAMPION',
    weight: 5,
    entrants: 1,
    survivors: 1,
  },
  {
    id: 'hall_of_fame',
    order: 12,
    kind: 'ceremony',
    label: 'Hall of Fame',
    shortLabel: 'HALL OF FAME',
    weight: 3.5,
    entrants: 0,
    survivors: 0,
  },
  {
    id: 'restart',
    order: 13,
    kind: 'ceremony',
    label: 'Next Season',
    shortLabel: 'RESTART',
    weight: 1.5,
    entrants: 0,
    survivors: 0,
  },
];

export const PHASE_IDS: readonly PhaseId[] = PHASES.map((p) => p.id);

const PHASE_BY_ID = new Map(PHASES.map((p) => [p.id, p]));

/** Resolves a phase in either mode, so shared code needs no mode branch. */
export function phaseDefinition(id: PhaseId): PhaseDefinition {
  const def = PHASE_BY_ID.get(id) ?? ROYALE_BY_ID.get(id);
  if (!def) throw new Error(`Unknown phase: ${id}`);
  return def;
}

/** Rounds that are played as head-to-head matches, in order. */
export const BRACKET_ROUNDS: readonly PhaseId[] = PHASES.filter((p) => p.kind === 'bracket').map(
  (p) => p.id,
);

export function nextPhase(id: PhaseId): PhaseId | null {
  const index = PHASES.findIndex((p) => p.id === id);
  if (index < 0) throw new Error(`Unknown phase: ${id}`);
  return PHASES[index + 1]?.id ?? null;
}

export interface ScheduledPhase {
  readonly id: PhaseId;
  readonly durationMs: number;
  /** Offset from the start of the season, in ms. */
  readonly offsetMs: number;
}

export interface Schedule {
  readonly totalMs: number;
  readonly phases: readonly ScheduledPhase[];
}

/**
 * Distribute `totalMinutes` across the phases in proportion to their weights.
 *
 * Rounding is accumulated rather than applied per phase, so the sum of the
 * phase durations is exactly the season duration - no drifting by a few hundred
 * milliseconds per phase, which over a 24/7 run would visibly desynchronise the
 * season clock from the phase clocks.
 */
export function buildSchedule(totalMinutes: number): Schedule {
  if (!(totalMinutes > 0))
    throw new RangeError(`totalMinutes must be positive, got ${totalMinutes}`);

  const totalMs = Math.round(totalMinutes * 60_000);
  const totalWeight = PHASES.reduce((sum, p) => sum + p.weight, 0);

  const phases: ScheduledPhase[] = [];
  let consumed = 0;
  let weightSoFar = 0;

  for (let i = 0; i < PHASES.length; i++) {
    const phase = PHASES[i]!;
    weightSoFar += phase.weight;
    const isLast = i === PHASES.length - 1;
    // Snap to the ideal cumulative boundary so rounding error never accumulates.
    const boundary = isLast ? totalMs : Math.round((weightSoFar / totalWeight) * totalMs);
    const durationMs = boundary - consumed;
    phases.push({ id: phase.id, durationMs, offsetMs: consumed });
    consumed = boundary;
  }

  return { totalMs, phases };
}

export function scheduledPhase(schedule: Schedule, id: PhaseId): ScheduledPhase {
  const found = schedule.phases.find((p) => p.id === id);
  if (!found) throw new Error(`Phase ${id} missing from schedule`);
  return found;
}
